import { ensureUserId } from '../utils/ensureUserId';

function sanitizeOrigin(o) {
  if (!o || typeof o !== 'string') return '';
  const s = o.trim();
  return /^https?:\/\//i.test(s) ? s.replace(/\/$/, '') : '';
}

// If we are on a Cloudflare Pages/Custom domain, DO NOT force a remote origin.
// Only use VITE_NCB_PROXY_ORIGIN when NOT on a function host (e.g., Greta/S3).
const functionHosts = new Set([
  'YOUR-PROJECT.pages.dev', // ⬅️ replace with your pages.dev
  'promptly.redhotteam.app' // ⬅️ keep only if this is mapped to the same Pages project
]);

const runtimeOrigin = (typeof window !== 'undefined' && (window.__NCB_ORIGIN || window.__VITE_NCB_PROXY_ORIGIN)) || '';
const buildOrigin = (import.meta?.env?.VITE_NCB_PROXY_ORIGIN) || '';

let ORIGIN = sanitizeOrigin(runtimeOrigin) || sanitizeOrigin(buildOrigin) || '';

if (typeof window !== 'undefined' && functionHosts.has(location.hostname)) {
  ORIGIN = ''; // same-origin on Cloudflare site
}

const NCB_BASE = ORIGIN ? `${ORIGIN}/api/_ncb` : '/api/_ncb';

if (typeof window !== 'undefined') window.__NCB_BASE__ = NCB_BASE;

async function ncbQuery(payload) {
  const res = await fetch(`${NCB_BASE}/query`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`NCB returned non-JSON (status ${res.status}): ${text.slice(0, 220)}…`);
  }
  if (!res.ok || json?.ok === false) throw new Error(`NCB error: ${json?.error || `HTTP ${res.status}`}`);
  return json?.data ?? json;
}

function buildName(d) {
  const c = d?.category === 'other' ? d?.customCategory : d?.category;
  const p = d?.projectType === 'other' ? d?.customProjectType : d?.projectType;
  const tags = [];
  if (d?.isPwa) tags.push('PWA');
  if (d?.hideFromSearchEngines) tags.push('NoSEO');
  if (d?.includeAdminConsole) tags.push('Admin');
  return `${c || 'misc'} - ${p || 'project'}${tags.length ? ' (' + tags.join(',') + ')' : ''}`;
}

function mapRow(r) {
  const jsonOr = (v, fallback) => {
    if (v == null) return fallback;
    if (typeof v !== 'string') return v; // assume it's already an object/array
    try { return JSON.parse(v); } catch { return fallback; }
  };

  return {
    id: r.id,
    name: r.name,
    prompt: r.encrypted_prompt || '',
    createdAt: r.created_at || r.updated_at || new Date().toISOString(),
    data: {
      category: r.category,
      projectType: r.project_type,
      complexity: r.complexity,
      framework: r.framework,
      styling: r.styling,
      features: jsonOr(r.features, []),
      customRequirements: r.custom_requirements || '',
      context: r.context || '',
      constraints: r.constraints || '',
      siteTitle: r.site_title || '',
      logoUrl: r.logo_url || '',
      faviconUrl: r.favicon_url || '',
      isPwa: !!r.is_pwa,
      hideFromSearchEngines: !!r.hide_from_search_engines,
      includeAdminConsole: !!r.include_admin_console,
      adminFeatures: jsonOr(r.admin_features, {}),
      adminCredentials: {
        useCustomCredentials: !!r.has_custom_credentials,
      },
    },
  };
}

export const PromptService = {
  async getUserPrompts(userIdArg, limit = 100) {
    const userId = userIdArg || ensureUserId();
    const r = await ncbQuery({
      action: 'read',
      table: 'prompt_templates_e4y8n3q5',
      filters: [{ key: 'user_id', value: userId }],
      order: { key: 'created_at', ascending: false },
      limit,
    });
    const rows = Array.isArray(r?.data) ? r.data : (r || []);
    return rows.map(mapRow);
  },

  async savePrompt(userIdArg, promptData, promptText) {
    const userId = userIdArg || ensureUserId();
    const strOrJson = (v) => {
      if (v == null) return '';
      if (typeof v === 'string') return v;
      try { return JSON.stringify(v); } catch { return String(v); }
    };

    const values = {
      user_id: userId,
      name: buildName(promptData),
      category: promptData?.category || null,
      project_type: promptData?.projectType || null,
      complexity: promptData?.complexity || null,
      framework: promptData?.framework || null,
      styling: promptData?.styling || null,
      features: strOrJson(promptData?.features),
      custom_requirements: promptData?.customRequirements || '',
      build_notes: promptData?.buildNotes || '',
      context: promptData?.context || '',
      constraints: promptData?.constraints || '',
      site_title: promptData?.siteTitle || '',
      logo_url: promptData?.logoUrl || '',
      favicon_url: promptData?.faviconUrl || '',
      is_pwa: !!promptData?.isPwa,
      hide_from_search_engines: !!promptData?.hideFromSearchEngines,
      include_admin_console: !!promptData?.includeAdminConsole,
      admin_features: strOrJson(promptData?.adminFeatures),
      has_custom_credentials: !!(promptData?.adminCredentials?.useCustomCredentials),
      encrypted_prompt: promptText,
      encryption_key_hash: null,
    };

    const created = await ncbQuery({ action: 'create', table: 'prompt_templates_e4y8n3q5', values });
    const row = Array.isArray(created?.data) ? created.data[0] : (created?.data || created);
    return row;
  },

  async deletePrompt(userIdArg, promptId) {
    const userId = userIdArg || ensureUserId();
    await ncbQuery({
      action: 'delete',
      table: 'prompt_templates_e4y8n3q5',
      id: promptId,
      // keep a guard filter so users can only delete their own rows
      filters: [{ key: 'user_id', value: userId }],
    });
    return true;
  },
};